/**
 * API client per la gestione dei casi.
 */

import axios from 'axios';

export interface CaseItem {
  id: string;
  patient_id: string;
  vet_id: string | null;
  vet_name: string | null;
  title: string | null;
  specialty: string | null;
  status: string;
  priority: string | null;
  opened_at: string;
  closed_at: string | null;
}

export interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  is_image: boolean;
  is_dicom: boolean;
}

export interface EmailItem {
  id: string;
  thread_id: string;
  message_id: string;
  direction: string | null;
  from_email: string;
  to_email: string[];
  subject: string | null;
  body_raw: string;
  body_html: string | null;
  summary_ai: string | null;
  tags: string[];
  category: string | null;
  priority: string | null;
  status: string | null;
  received_at: string | null;
  created_at: string | null;
  attachments: Attachment[];
}

export interface ThreadItem {
  id: string;
  message_id: string;
  subject: string | null;
  first_received_at: string | null;
  emails: EmailItem[];
}

export interface CaseDetail extends CaseItem {
  patient_name: string | null;
  patient_species: string | null;
  patient_breed: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  threads: ThreadItem[];
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const casesApi = {
  list: async (filters?: { status?: string; vet_id?: string }): Promise<CaseItem[]> => {
    const params: Record<string, string> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.vet_id) params.vet_id = filters.vet_id;
    const response = await api.get<CaseItem[]>('/cases/', { params });
    return response.data;
  },

  get: async (caseId: string): Promise<CaseDetail> => {
    const response = await api.get<CaseDetail>(`/cases/${caseId}`);
    return response.data;
  },

  assignVet: async (caseId: string, vetId: string): Promise<{ message: string; vet_id: string; vet_name: string }> => {
    const response = await api.post<{ message: string; vet_id: string; vet_name: string }>(`/cases/${caseId}/assign`, {
      vet_id: vetId,
    });
    return response.data;
  },

  closeCase: async (caseId: string): Promise<{ message: string; status: string }> => {
    const response = await api.patch<{ message: string; status: string }>(`/cases/${caseId}`, {
      status: 'closed',
    });
    return response.data;
  },

  getAuditTrail: async (caseId: string): Promise<CaseAuditTrail> => {
    const response = await api.get<CaseAuditTrail>(`/cases/${caseId}/audit`);
    return response.data;
  },
};

export interface CaseAuditLogItem {
  id: string;
  email_id: string | null;
  email_subject: string | null;
  step_name: string;
  timestamp: string;
  input_data: Record<string, unknown> | null;
  output_data: Record<string, unknown> | null;
  llm_prompt: Record<string, unknown> | null;
  llm_response: Record<string, unknown> | null;
  decision: string | null;
  extra_data: Record<string, unknown> | null;
}

export interface CaseAuditTrail {
  case_id: string;
  case_title: string | null;
  case_status: string;
  case_specialty: string | null;
  patient_name: string | null;
  vet_name: string | null;
  logs: CaseAuditLogItem[];
  email_count: number;
  log_count: number;
}
